import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { Users, Building, Briefcase, TrendingUp, DollarSign } from 'lucide-react';

const Overview = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]"></div></div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Overview</h1>
        <p className="text-lg text-slate-600 mb-8">Real-time analytics and insights</p>

        {/* KPI Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-[#0D9488]" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {analytics.total_apprentices}
            </div>
            <div className="text-sm text-slate-500">Total Apprentices</div>
            <div className="text-xs text-green-600 mt-2">{analytics.paid_apprentices} paid</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Building className="w-8 h-8 text-[#0F172A]" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {analytics.total_employers}
            </div>
            <div className="text-sm text-slate-500">Total Employers</div>
            <div className="text-xs text-green-600 mt-2">{analytics.subscribed_employers} subscribed</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Briefcase className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {analytics.total_matches}
            </div>
            <div className="text-sm text-slate-500">Total Matches</div>
            <div className="text-xs text-green-600 mt-2">{analytics.interview_matches} interviews</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              €{analytics.revenue?.toLocaleString() || 0}
            </div>
            <div className="text-sm text-slate-500">Total Revenue</div>
          </div>
        </div>

        {/* Funnel Chart */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Apprentice Funnel</h2>
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="w-32 text-sm text-slate-600">Total Signups</div>
              <div className="flex-1">
                <div className="h-10 bg-blue-500 rounded-r-lg" style={{ width: '100%' }}></div>
              </div>
              <div className="w-16 text-right font-semibold">{analytics.total_apprentices}</div>
            </div>
            <div className="flex items-center">
              <div className="w-32 text-sm text-slate-600">Paid</div>
              <div className="flex-1">
                <div className="h-10 bg-purple-500 rounded-r-lg" style={{ width: `${(analytics.paid_apprentices / analytics.total_apprentices) * 100}%` }}></div>
              </div>
              <div className="w-16 text-right font-semibold">{analytics.paid_apprentices}</div>
            </div>
            <div className="flex items-center">
              <div className="w-32 text-sm text-slate-600">Completed</div>
              <div className="flex-1">
                <div className="h-10 bg-green-500 rounded-r-lg" style={{ width: `${(analytics.completed_assessments / analytics.total_apprentices) * 100}%` }}></div>
              </div>
              <div className="w-16 text-right font-semibold">{analytics.completed_assessments}</div>
            </div>
            <div className="flex items-center">
              <div className="w-32 text-sm text-slate-600">Matched</div>
              <div className="flex-1">
                <div className="h-10 bg-teal-500 rounded-r-lg" style={{ width: `${(analytics.total_matches / analytics.total_apprentices) * 20}%` }}></div>
              </div>
              <div className="w-16 text-right font-semibold">{analytics.total_matches}</div>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Health</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Interview Rate</span>
                <span className="font-semibold text-green-600">{analytics.interview_rate}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Active Roles</span>
                <span className="font-semibold">{analytics.total_roles}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Avg Match Score</span>
                <span className="font-semibold">78%</span>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Activity</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center text-slate-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                {analytics.interview_matches} interviews scheduled this week
              </div>
              <div className="flex items-center text-slate-600">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                {analytics.total_roles} active job postings
              </div>
              <div className="flex items-center text-slate-600">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                {analytics.subscribed_employers} active subscriptions
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
